import React from 'react';
import { ShieldCheck, FileCheck, ClipboardList, BadgeCheck, Building2, Flame, ArrowUpRight } from "lucide-react";

const credentials = [
  {
    icon: ShieldCheck,
    title: "$20M Public Liability Insurance",
    description: "Fully insured on every job — commercial and residential.",
  },
  {
    icon: FileCheck,
    title: "Australian Standards Compliant",
    description: "All work is completed to AS/NZS plumbing standards and local council requirements.",
  },
  {
    icon: ClipboardList,
    title: "QBCC Licensed Contractor",
    description: "Queensland licensed and registered — you're covered from first fix to final inspection.",
  },
];

const verifications = [
  {
    icon: BadgeCheck,
    title: "QBCC Licence No. 15538687",
    sub: "Queensland Building and Construction Commission Licensed Contractor",
    href: "https://my.qbcc.qld.gov.au/myQBCC/s/qbcc-licensee-register",
  },
  {
    icon: Building2,
    title: "ABN 89 688 371 851",
    sub: "OLIVER & SONS PTY LTD",
    href: "https://abr.business.gov.au/ABN/View?id=89688371851",
  },
  {
    icon: Building2,
    title: "ACN 688 371 851",
    sub: "OLIVER & SONS PTY LTD",
    href: "https://www.asic.gov.au/online-services/search-asic-registers/company-and-organisation-registers/",
  },
  {
    icon: Flame,
    title: "Gas Fitting Licence",
    sub: "Licensed for gas fitting work",
    href: "https://rshq-eos.powerappsportals.com/search-licence/",
  },
];

export default function ComplianceSection() {
  return (
    <section className="relative overflow-hidden" style={{ backgroundColor: '#0d1f33' }}>
      {/* Top blue border */}
      <div className="absolute top-0 left-0 right-0 h-[2px] bg-[#2563eb]" />

      {/* Subtle grid texture overlay */}
      <div
        className="absolute inset-0 pointer-events-none opacity-[0.04]"
        style={{
          backgroundImage:
            'linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)',
          backgroundSize: '40px 40px',
        }}
      />

      {/* Diagonal gradient for depth */}
      <div className="absolute inset-0 pointer-events-none bg-gradient-to-br from-[#0a1628]/60 via-transparent to-[#0d1f33]/80" />

      <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-6 md:px-14 lg:px-20 py-14 md:py-20">
        <div className="grid md:grid-cols-2 gap-10 md:gap-16 items-center">

          {/* LEFT COLUMN */}
          <div>
            <h2 className="font-bold text-white leading-[1.1] mb-6 tracking-tight" style={{ fontSize: 'clamp(1.6rem, 5vw, 3rem)' }}>
              Licensed, Insured<br />
              <span className="text-white">and Done Right.</span>
            </h2>
            <p className="text-gray-400 text-base leading-relaxed mb-8 max-w-md">
              We bring over 30 years of plumbing experience to every job. Our commercial plumbing work is carried out by appropriately licensed tradespeople and in accordance with applicable Australian Standards and project requirements.
            </p>

            {/* Blue separator */}
            <div className="w-full h-px bg-gradient-to-r from-[#2563eb] via-[#2563eb]/40 to-transparent mb-10" />

            {/* Verification items */}
            <div className="space-y-8">
              {verifications.map((item, index) => (
                <div key={index} className="flex items-start gap-3">
                  <item.icon className="w-6 h-6 text-[#2563eb] flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-white text-xl font-bold tracking-wide">{item.title}</p>
                    <p className="text-gray-400 text-sm mt-1">{item.sub}</p>
                    <a
                      href={item.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 text-[#60a5fa] hover:text-white text-xs font-semibold tracking-wide mt-3 px-3.5 py-2 rounded-md border border-[#2563eb]/40 hover:border-[#2563eb] hover:bg-[#2563eb]/10 transition-all duration-200"
                    >
                      {index === 0 ? 'Verify QBCC Licence' : index === 1 ? 'Verify ABN' : index === 2 ? 'Verify ACN' : 'Verify Gas Licence'} <ArrowUpRight className="w-3.5 h-3.5" />
                    </a>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* RIGHT COLUMN */}
          <div className="flex flex-col gap-0">
            {credentials.map((item, index) => (
              <div key={index} className="flex items-start gap-5 py-8 border-b border-white/5 last:border-b-0 group">
                {/* Blue vertical bar */}
                <div className="w-[4px] self-stretch bg-[#2563eb] rounded-full flex-shrink-0 opacity-80 group-hover:opacity-100 transition-opacity duration-300" />

                {/* Icon */}
                <div className="w-10 h-10 bg-[#1e3a5f]/40 border border-[#2563eb]/20 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:border-[#2563eb]/50 group-hover:bg-[#1e3a5f]/60 transition-all duration-300">
                  <item.icon className="w-5 h-5 text-[#5a9cf8]" />
                </div>

                {/* Text */}
                <div>
                  <h3 className="text-white font-bold text-base mb-1 leading-snug">{item.title}</h3>
                  <p className="text-gray-400 text-sm leading-relaxed">{item.description}</p>
                </div>
              </div>
            ))}
          </div>

        </div>
      </div>
    </section>
  );
}